import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { updateUserSettings } from "@dataconnect/generated";
import { auth, functions } from "../config/firebase";
import { AppError } from "../utils/appError";
import i18n from "../i18n";
import { createLanguageSync, normalizeLanguage } from "../utils/languageSync";
import type { LanguageSync, SupportedLanguage } from "../utils/languageSync";
import { reportError } from "../utils/errorReporting";

/**
 * The user's current UI language as a persistable "en"/"de" code, or null
 * when it can't be determined. Shared with initializeUser, which seeds the
 * same value into the UpsertUser call so brand-new accounts get their
 * language atomically with row creation.
 */
export function getCurrentNormalizedLanguage(): SupportedLanguage | null {
  return normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);
}

let syncInstance: LanguageSync | null = null;

/**
 * Called by initializeUser after UpsertUser completes. The upsert carries
 * its own language snapshot on every login, so one that lands after a newer
 * sync write would silently revert the column; this forces a reconcile that
 * re-asserts the latest selection (PR #993 review).
 */
export function notifyLanguageSyncProfileUpserted(): void {
  syncInstance?.handleProfileUpserted();
}

let initialized = false;

/**
 * Mirrors the UI language onto the User row so backend senders can localize
 * automatic emails (SIS-483). Write-only from the app's perspective: display
 * language stays owned by i18next/localStorage, the DB value is never read
 * back. Runs outside the useUserSettings save coordinator (different
 * columns); the one other language writer — initializeUser's UpsertUser
 * seed — is reconciled via notifyLanguageSyncProfileUpserted above.
 *
 * The same signal is mirrored to HubSpot's "Preferred language" so the
 * newsletter can be localized (SIS-578). The sign-in language travels with
 * initializeUser's upsertHubspotContact call at no extra cost; only a later
 * in-session switch goes through the narrow updateHubspotContactLanguage
 * callable, and only once the DB write has confirmed it.
 */
export function initLanguageSync(): void {
  if (initialized) return;
  initialized = true;
  const sync = createLanguageSync({
    getLanguage: () => i18n.resolvedLanguage ?? i18n.language,
    // user_updateMany reports how many rows it touched; 0 means the User
    // row doesn't exist yet (first signup) and the sync must keep retrying.
    pushLanguage: (language) =>
      updateUserSettings({ language }).then((res) => res.data.user_updateMany),
    onLanguageChanged: async (language) => {
      // The callable requires a verified email (authentication alone does
      // not prove a password sign-up owns its address). Don't send a call
      // that will be refused: after verification the next login's upsert
      // carries the then-current language anyway.
      if (auth.currentUser?.emailVerified !== true) return;
      const updateHubspotContactLanguage = httpsCallable<
        { language: SupportedLanguage },
        { success: boolean; error?: string }
      >(functions, "updateHubspotContactLanguage");
      const result = await updateHubspotContactLanguage({ language });
      // The callable reports HubSpot failures instead of throwing (Sentry
      // already has them server-side); surface one here so the sync
      // retries on its backoff schedule instead of counting it as done.
      if (!result.data.success) {
        throw new AppError(
          "hubspot/language_sync_failed",
          result.data.error ?? "updateHubspotContactLanguage failed"
        );
      }
    },
    onError: (err) => reportError(err, { context: "languageSync:push" }),
  });
  syncInstance = sync;

  // Support impersonation signs in with the customer's UID plus an
  // `impersonatedBy` custom claim (see src/routes/__root.tsx, which skips
  // initializeUser/analytics the same way). Never sync the agent's browser
  // language onto the customer's row. Writes are gated synchronously on
  // every auth event and only re-enabled once the async claim check clears
  // — otherwise a language event during the agent → customer transition
  // could still fire a mutation under the customer's token (PR #993
  // review). The epoch counter drops stale callbacks that resolve after a
  // newer auth event.
  let authEpoch = 0;
  onAuthStateChanged(auth, (user) => {
    const epoch = ++authEpoch;
    sync.handleAuthChanged(null);
    if (!user) return;
    user.getIdTokenResult().then(
      (token) => {
        if (epoch !== authEpoch) return;
        if (!token.claims.impersonatedBy) sync.handleAuthChanged(user.uid);
      },
      () => {
        // Can't prove this isn't impersonation — stay gated; the next auth
        // event or login retries.
      }
    );
  });
  i18n.on("languageChanged", () => sync.handleLanguageChanged());
  // A language switch during an outage exhausts the bounded retry budget;
  // reconnecting is the natural moment to try again.
  window.addEventListener("online", () => sync.handleLanguageChanged());
}
